import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../../lib/constants';
import { useNotification } from '../../../../contexts/NotificationContext';
import { getCookie } from '../../../../lib/utils';

export const useRole = (role_id) => {
  const { showNotification } = useNotification();
  const isEdit = !!role_id;
  const [loading, setLoading] = useState(isEdit);
  const [existingData, setExistingData] = useState(null);

  useEffect(() => {
    if (!isEdit) {
      setLoading(false);
      setExistingData(null);
      return;
    }
    setLoading(true);
    axios.get(`${API_BASE_URL}/get_role/${role_id}`, { 
      withCredentials: true,
      headers: { 'X-CSRF-TOKEN': getCookie('csrf_access_token') }
    })
      .then(res => {
        const r = res.data.data || res.data;
        setExistingData({
          name: r.name || '',
          description: r.description || '',
          permissions: Array.isArray(r.permissions) ? r.permissions : [],
        });
      })
      .catch(() => showNotification('error', 'Failed to fetch role details.'))
      .finally(() => setLoading(false));
  }, [role_id, isEdit, showNotification]);

  const submit = async (payload) => isEdit
    ? axios.put(`${API_BASE_URL}/update_role/${role_id}`, payload, { headers: { 'X-CSRF-TOKEN': getCookie('csrf_access_token') }, withCredentials: true })
    : axios.post(`${API_BASE_URL}/add_role`, payload, { headers: { 'X-CSRF-TOKEN': getCookie('csrf_access_token') }, withCredentials: true });

  return { isEdit, loading, existingData, submit };
};

/**
 * Role List service hook — fetch all + delete.
 */
export const useRoleList = () => {
  const { showNotification } = useNotification();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRoles = useCallback(() => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/get_roles`, { 
      withCredentials: true,
      headers: { 'X-CSRF-TOKEN': getCookie('csrf_access_token') }
    })
      .then(res => {
        const data = res.data.data || res.data;
        setRoles(Array.isArray(data) ? data : []);
      })
      .catch(() => showNotification('error', 'Failed to fetch roles.'))
      .finally(() => setLoading(false));
  }, [showNotification]);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const deleteRole = async (role_id) => {
    const res = await axios.delete(`${API_BASE_URL}/delete_role/${role_id}`, { 
      withCredentials: true,
      headers: { 'X-CSRF-TOKEN': getCookie('csrf_access_token') }
    });
    if (res.status === 200) setRoles(prev => prev.filter(r => r.role_id !== role_id));
  };

  return { roles, loading, fetchRoles, deleteRole };
};
